import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axiosConfig';
import { addSellerResponse, flagReview } from './slices/reviewSlice';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
const PROMOTION_STORAGE_KEY = 'global_promotions';
const ORDERS_CACHE_KEY = 'seller_orders_cache';
const ORDERS_CACHE_TIMESTAMP_KEY = 'seller_orders_cache_timestamp';
const CACHE_VALIDITY_MS = 5 * 60 * 1000; // 5 minutes cache validity

const loadStoredPromotions = () => {
  try {
    const stored = localStorage.getItem(PROMOTION_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load promotions from storage:', error);
    return [];
  }
};

const saveStoredPromotions = (promotions) => {
  try {
    localStorage.setItem(PROMOTION_STORAGE_KEY, JSON.stringify(promotions || []));
  } catch (error) {
    console.error('Failed to save promotions to storage:', error);
  }
};

// Cache functions for orders to enable instant chart loading
const loadCachedOrders = () => {
  try {
    const cached = localStorage.getItem(ORDERS_CACHE_KEY);
    const timestamp = localStorage.getItem(ORDERS_CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) return [];
    
    // Check if cache is still valid (less than 5 minutes old)
    const cacheAge = Date.now() - parseInt(timestamp, 10);
    if (cacheAge > CACHE_VALIDITY_MS) {
      // Cache expired, clear it
      localStorage.removeItem(ORDERS_CACHE_KEY);
      localStorage.removeItem(ORDERS_CACHE_TIMESTAMP_KEY);
      return [];
    }
    
    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load cached orders:', error);
    return [];
  }
};

const saveCachedOrders = (orders) => {
  try {
    localStorage.setItem(ORDERS_CACHE_KEY, JSON.stringify(orders || []));
    localStorage.setItem(ORDERS_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Failed to save orders to cache:', error);
  }
};

const filterPromotionsBySeller = (sellerId, promotions) =>
  promotions.filter((promo) => promo.sellerId === sellerId);

const sanitizeProductImage = (image = {}) => {
  if (!image || typeof image !== 'object') return null;
  const {
    id,
    imageUrl,
    thumbnailUrl,
    altText,
    caption,
    imageType,
    displayOrder,
    primaryImage,
    isPrimary,
  } = image;

  const sanitized = {};
  if (id !== undefined) sanitized.id = id;
  if (imageUrl !== undefined) sanitized.imageUrl = imageUrl;
  if (thumbnailUrl !== undefined) sanitized.thumbnailUrl = thumbnailUrl;
  if (altText !== undefined) sanitized.altText = altText;
  if (caption !== undefined) sanitized.caption = caption;
  if (imageType !== undefined) sanitized.imageType = imageType;
  if (displayOrder !== undefined) sanitized.displayOrder = displayOrder;
  if (primaryImage !== undefined) sanitized.primaryImage = primaryImage;
  if (isPrimary !== undefined) sanitized.isPrimary = isPrimary;

  return sanitized;
};

const allowedSpecificationsKeys = new Set([
  'hardness',
  'maintenanceRequirements',
  'grainDirection',
  'width',
  'pattern',
  'finish',
  'porosity',
  'recyclability',
  'slipResistance',
  'floorUse',
  'frostResistance',
  'flexuralStrength',
  'color',
  'environmentalImpact',
  'length',
  'material',
  'density',
  'outdoorUse',
  'surfaceTexture',
  'quarryLocation',
  'thickness',
  'additionalProperties',
  'qualityCertification',
  'cleaningInstructions',
  'kitchenUse',
  'sealingRequired',
  'waterAbsorption',
  'sustainabilityCertification',
  'compressiveStrength',
  'bathroomUse',
  'gradeClassification',
  'processingLocation',
  'indoorUse',
  'installationMethod',
  'edgeType',
  'wallUse',
]);

const sanitizeSpecifications = (specifications = {}) => {
  if (!specifications || typeof specifications !== 'object') return {};
  return Object.keys(specifications).reduce((acc, key) => {
    if (allowedSpecificationsKeys.has(key)) {
      acc[key] = specifications[key];
    }
    return acc;
  }, {});
};

// Replace a review in state.reviews with the updated copy returned by the backend
const mergeUpdatedReview = (state, payload) => {
  const updated = payload?.review;
  if (!updated?.id || !Array.isArray(state.reviews)) return;
  const index = state.reviews.findIndex((r) => r.id === updated.id);
  if (index !== -1) {
    // Keep client-side enrichment (e.g. product name) that the raw review may lack
    const current = state.reviews[index];
    state.reviews[index] = {
      ...current,
      ...updated,
      attributes: { ...(current.attributes || {}), ...(updated.attributes || {}) },
    };
  }
};

// Async thunks for seller API calls
export const fetchSellerReviews = createAsyncThunk(
  'seller/fetchReviews',
  async ({ sellerId, page = 0, size = 10, approvedOnly = true }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/reviews/seller/${sellerId}`,
        {
          params: { page, size, approvedOnly },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSellerOrders = createAsyncThunk(
  'seller/fetchOrders',
  async ({ sellerId, page = 0, size = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orders/seller/${sellerId}`,
        {
          params: { page, size },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSellerPendingOrders = createAsyncThunk(
  'seller/fetchPendingOrders',
  async ({ sellerId }, { rejectWithValue }) => {
    try {
      console.log('Fetching pending orders for seller:', sellerId);
      const response = await axios.get(
        `${API_BASE_URL}/orders/seller/${sellerId}/pending`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSellerProducts = createAsyncThunk(
  'seller/fetchProducts',
  async ({ 
    sellerId, 
    status = 'all', 
    approvalStatus = null, 
    page = 0, 
    size = 20,
    search = null,
    category = null,
    material = null,
    finish = null,
    origin = null,
    sortBy = 'createdAt',
    sortDir = 'desc'
  }, { rejectWithValue }) => {
    try {
      console.log('Making API call to fetch seller products:', {
        sellerId,
        status,
        approvalStatus,
        page,
        size,
        search,
        category,
        material,
        finish,
        origin,
        sortBy,
        sortDir,
        url: `${API_BASE_URL}/products/seller/${sellerId}`,
        token: localStorage.getItem('token') ? 'Token exists' : 'No token'
      });
      
      const params = { 
        status,
        page,
        size,
        sortBy,
        sortDir
      };
      
      if (approvalStatus) {
        params.approvalStatus = approvalStatus;
      }
      if (search) {
        params.search = search;
      }
      if (category) {
        params.category = category;
      }
      if (material) {
        params.material = material;
      }
      if (finish) {
        params.finish = finish;
      }
      if (origin) {
        params.origin = origin;
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/products/seller/${sellerId}`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('API response for fetchSellerProducts:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in fetchSellerProducts:', error);
      console.error('Error response:', error.response?.data);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSellerPendingProducts = createAsyncThunk(
  'seller/fetchPendingProducts',
  async ({ sellerId, search = null, category = null }, { rejectWithValue }) => {
    try {
      const params = { 
        status: 'ALL',
        approvalStatus: 'PENDING',
        size: 1000 // Set a large size to get all pending products
      };
      
      if (search) {
        params.search = search;
      }
      if (category) {
        params.category = category;
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/products/seller/${sellerId}`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSellerApprovedProducts = createAsyncThunk(
  'seller/fetchApprovedProducts',
  async ({ sellerId, search = null, category = null }, { rejectWithValue }) => {
    try {
      const params = { 
        status: 'ALL',
        approvalStatus: 'APPROVED',
        size: 1000 // Set a large size to get all approved products
      };
      
      if (search) {
        params.search = search;
      }
      if (category) {
        params.category = category;
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/products/seller/${sellerId}`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSellerRejectedProducts = createAsyncThunk(
  'seller/fetchRejectedProducts',
  async ({ sellerId, search = null, category = null }, { rejectWithValue }) => {
    try {
      const params = {
        status: 'ALL',
        approvalStatus: 'REJECTED',
        size: 1000
      };
      
      if (search) {
        params.search = search;
      }
      if (category) {
        params.category = category;
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/products/seller/${sellerId}`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const loadSellerPromotions = createAsyncThunk(
  'seller/loadPromotions',
  async (sellerId, { rejectWithValue }) => {
    try {
      if (!sellerId) return [];
      const response = await axios.get(
        `${API_BASE_URL}/promotions/seller/${sellerId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data.promotions || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to load promotions');
    }
  }
);

// New: Create product
export const createSellerProduct = createAsyncThunk(
  'seller/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/products`,
        productData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data; // expected: { success, message, product }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product');
    }
  }
);

// Create product with images
export const createSellerProductWithImages = createAsyncThunk(
  'seller/createProductWithImages',
  async (formData, { rejectWithValue }) => {
    try {
      // Debug: Log FormData contents
      console.log('Sending FormData with keys:', Array.from(formData.keys()));
      const productDataValue = formData.get('productData');
      console.log('productData value:', productDataValue ? (productDataValue.length > 100 ? productDataValue.substring(0, 100) + '...' : productDataValue) : 'MISSING');
      console.log('productData type:', typeof productDataValue);
      
      // Verify productData exists before sending
      if (!productDataValue || (typeof productDataValue === 'string' && productDataValue.trim() === '')) {
        console.error('productData is missing or empty in FormData!');
        return rejectWithValue('Product data is missing. Please try again.');
      }
      
      // Axios interceptor will handle FormData Content-Type automatically
      // Just send FormData - axios will detect it and set multipart/form-data with boundary
      const response = await axios.post(
        `${API_BASE_URL}/products/with-images`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
            // Content-Type will be set automatically by axios for FormData (handled in interceptor)
          }
        }
      );
      return response.data; // expected: { success, message, product }
    } catch (error) {
      console.error('Error creating product with images:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error request config:', error.config);
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create product');
    }
  }
);

// Update product
export const updateSellerProduct = createAsyncThunk(
  'seller/updateProduct',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/products/${productId}`,
        productData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data; // expected: { success, message, product }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product');
    }
  }
);

// Delete product
export const deleteSellerProduct = createAsyncThunk(
  'seller/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/products/${productId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return { productId, ...response.data }; // Return productId for local state update
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete product');
    }
  }
);

// Duplicate product
export const duplicateSellerProduct = createAsyncThunk(
  'seller/duplicateProduct',
  async (product, { rejectWithValue }) => {
    try {
      // Create a clean copy of the product with only the necessary fields
      const duplicatedProduct = {
        // Basic product information
        name: `${product.name} (Copy)`,
        description: product.description,
        category: product.category,
        subCategory: product.subCategory,
        price: product.price,
        priceUnit: product.priceUnit,
        
        // Inventory
        stockQuantity: product.stockQuantity,
        stockUnit: product.stockUnit,
        maximumOrderQuantity: product.maximumOrderQuantity,
        
        // Product specifications (if exists) - clean calculated fields
        ...(product.specifications && { 
          specifications: {
            // Basic specifications
            ...(product.specifications.material && { material: product.specifications.material }),
            ...(product.specifications.color && { color: product.specifications.color }),
            ...(product.specifications.finish && { finish: product.specifications.finish }),
            ...(product.specifications.pattern && { pattern: product.specifications.pattern }),
            ...(product.specifications.style && { style: product.specifications.style }),
            ...(product.specifications.brand && { brand: product.specifications.brand }),
            ...(product.specifications.model && { model: product.specifications.model }),
            
            // Dimensions
            ...(product.specifications.length && { length: product.specifications.length }),
            ...(product.specifications.width && { width: product.specifications.width }),
            ...(product.specifications.thickness && { thickness: product.specifications.thickness }),
            ...(product.specifications.unit && { unit: product.specifications.unit }),
            
            // Physical properties
            ...(product.specifications.weight && { weight: product.specifications.weight }),
            ...(product.specifications.density && { density: product.specifications.density }),
            ...(product.specifications.porosity && { porosity: product.specifications.porosity }),
            ...(product.specifications.waterAbsorption && { waterAbsorption: product.specifications.waterAbsorption }),
            ...(product.specifications.slipResistance && { slipResistance: product.specifications.slipResistance }),
            ...(product.specifications.frostResistance && { frostResistance: product.specifications.frostResistance }),
            
            // Usage specifications
            ...(product.specifications.indoorUse && { indoorUse: product.specifications.indoorUse }),
            ...(product.specifications.outdoorUse && { outdoorUse: product.specifications.outdoorUse }),
            ...(product.specifications.floorUse && { floorUse: product.specifications.floorUse }),
            ...(product.specifications.wallUse && { wallUse: product.specifications.wallUse }),
            ...(product.specifications.kitchenUse && { kitchenUse: product.specifications.kitchenUse }),
            ...(product.specifications.bathroomUse && { bathroomUse: product.specifications.bathroomUse }),
            
            // Additional properties
            ...(product.specifications.additionalProperties && { additionalProperties: product.specifications.additionalProperties })
            
            // Explicitly exclude calculated fields:
            // - areaInSquareMeters (calculated from length * width)
            // - volumeInCubicMeters (calculated from length * width * thickness)
          }
        }),
        
        // Location and availability
        ...(product.origin && { origin: product.origin }),
        ...(product.availableLocations && { availableLocations: product.availableLocations }),
        ...(product.isCustomizable !== undefined && { isCustomizable: product.isCustomizable }),
        
        // Logistics (if exists)
        ...(product.weight && { weight: product.weight }),
        ...(product.dimensions && { 
          dimensions: {
            // Basic dimensions
            ...(product.dimensions.length && { length: product.dimensions.length }),
            ...(product.dimensions.width && { width: product.dimensions.width }),
            ...(product.dimensions.height && { height: product.dimensions.height }),
            ...(product.dimensions.thickness && { thickness: product.dimensions.thickness }),
            ...(product.dimensions.unit && { unit: product.dimensions.unit }),
            
            // Package dimensions
            ...(product.dimensions.packageLength && { packageLength: product.dimensions.packageLength }),
            ...(product.dimensions.packageWidth && { packageWidth: product.dimensions.packageWidth }),
            ...(product.dimensions.packageHeight && { packageHeight: product.dimensions.packageHeight }),
            ...(product.dimensions.packageUnit && { packageUnit: product.dimensions.packageUnit })
            
            // Explicitly exclude calculated fields:
            // - area (calculated from length * width)
            // - volume (calculated from length * width * height)
            // - packageVolume (calculated from package dimensions)
            // - dimensionsString (formatted string)
            // - packageDimensionsString (formatted string)
          }
        }),
        ...(product.packagingType && { packagingType: product.packagingType }),
        ...(product.shippingWeight && { shippingWeight: product.shippingWeight }),
        
        // Lot information (if exists)
        ...(product.lotSize && { lotSize: product.lotSize }),
        ...(product.lotPercentage && { lotPercentage: product.lotPercentage }),
        ...(product.lotLength && { lotLength: product.lotLength }),
        ...(product.lotWidth && { lotWidth: product.lotWidth }),
        ...(product.lotNos && { lotNos: product.lotNos }),
        ...(product.lotSft && { lotSft: product.lotSft }),
        ...(product.lotLocation && { lotLocation: product.lotLocation }),
        // Multi-lot array (always include so it overwrites any previous lots)
        ...(product.lots !== undefined && { lots: product.lots }),
        
        // Quality and certifications (if exists)
        ...(product.certifications && { certifications: product.certifications }),
        ...(product.qualityGrade && { qualityGrade: product.qualityGrade }),
        
        // SEO and marketing (if exists)
        ...(product.tags && { tags: product.tags }),
        ...(product.seoTitle && { seoTitle: product.seoTitle }),
        ...(product.seoDescription && { seoDescription: product.seoDescription }),
        ...(product.keywords && { keywords: product.keywords }),
        
        // Clean up images array to remove frontend-only fields
        images: product.images ? product.images.map(image => {
          const { primary, aspectRatio, ...cleanImage } = image;
          return cleanImage;
        }) : [],
        
        // Video URLs (if exists)
        ...(product.videoUrls && { videoUrls: product.videoUrls }),
        
        // Set default values for new product
        status: 'DRAFT',
        approvalStatus: 'PENDING',
        featured: false,
        sponsored: false,
        discountPercentage: 0
        
        // Explicitly exclude system-generated and problematic fields:
        // - id, createdAt, updatedAt (system-generated)
        // - analytics (will be created fresh)
        // - approvalComments, approvedBy, approvedAt (approval-related)
        // - primaryImageUrl (will be set by backend)
        // - sellerId, sellerName (will be set by backend from auth)
        // - discountedPrice, discountValidUntil (pricing-related)
        };

      // Filter out undefined and null values to prevent backend validation errors
      const cleanedProduct = Object.fromEntries(
        Object.entries(duplicatedProduct).filter(([key, value]) => {
          // Keep the field if:
          // 1. Value is not null or undefined
          // 2. Value is a boolean (including false)
          // 3. Value is a number (including 0)
          // 4. Value is an empty string (some fields might need empty strings)
          // 5. Value is an empty array (some fields might need empty arrays)
          return value !== null && 
                 value !== undefined && 
                 (typeof value === 'boolean' || 
                  typeof value === 'number' || 
                  typeof value === 'string' || 
                  Array.isArray(value) || 
                  (typeof value === 'object' && Object.keys(value).length > 0));
        })
      );

      const response = await axios.post(
        `${API_BASE_URL}/products`,
        cleanedProduct,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data; // expected: { success, message, product }
    } catch (error) {
      console.error('Duplicate product error:', error);
      
      // Provide detailed error information
      let errorMessage = 'Failed to duplicate product';
      
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.details) {
          errorMessage = `Validation error: ${error.response.data.details}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchSellerStats = createAsyncThunk(
  'seller/fetchStats',
  async (sellerId, { dispatch, rejectWithValue }) => {
    try {
      // Fetch reviews analytics only (non-blocking for UI)
      // Don't fetch pendingOrders and products here to avoid duplicate calls
      // and to prevent blocking the dashboard loading
      const reviewsPromise = dispatch(fetchSellerReviews({ sellerId, size: 1 }));
      
      // Wait only for reviews to settle (fast operation)
      const results = await Promise.allSettled([
        reviewsPromise
      ]);
      
      // Note: pendingOrders is fetched separately in dashboard useEffect
      // to avoid blocking and allow charts to load instantly
      
      return {
        reviews: results[0]?.value?.payload || null,
        pendingOrders: null, // Will be fetched separately
        products: null // Will be fetched separately
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const applyPromotion = createAsyncThunk(
  'seller/applyPromotion',
  async (
    { sellerId, promotionId, title, productIds, discount, discountType, discountAmount, startDate, startTime, endDate, endTime, status },
    { rejectWithValue, getState }
  ) => {
    try {
      if (!sellerId) {
        throw new Error('Missing seller information for promotion');
      }

      const normalizedProductIds = Array.isArray(productIds)
        ? productIds.map((pid) => String(pid))
        : [];

      if (normalizedProductIds.length === 0) {
        throw new Error('Select at least one product for the promotion');
      }

      // Use products already in state to avoid re-fetching
      const state = getState();
      const productSources = [
        ...(state.seller.products || []),
        ...(state.seller.approvedProducts || []),
        ...(state.seller.pendingProducts || []),
        ...(state.seller.rejectedProducts || []),
      ];

      const selectedProducts = productSources.filter((product) =>
        normalizedProductIds.includes(String(product.id ?? product._id))
      );

      if (selectedProducts.length === 0) {
        throw new Error('Selected products are no longer available');
      }

      // Prepare promotion data for API
      const promotionData = {
        title: title || `Promotion ${discount || discountAmount || 0}${discountType === 'amount' ? '₹' : '%'}`,
        discountType: discountType || 'percentage',
        discount: discountType === 'percentage' ? Number(discount) || null : null,
        discountAmount: discountType === 'amount' ? Number(discountAmount) || null : null,
        startDate: startDate || null,
        startTime: startTime || null,
        endDate: endDate || null,
        endTime: endTime || null,
        productIds: normalizedProductIds,
        // Only set status for new promotions, preserve existing status for updates
        ...(promotionId ? {} : { status: status || 'active' }),
      };

      let response;
      if (promotionId) {
        // Update existing promotion
        response = await axios.put(
          `${API_BASE_URL}/promotions/${promotionId}`,
          promotionData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new promotion
        response = await axios.post(
          `${API_BASE_URL}/promotions`,
          promotionData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      const promotionRecord = response.data.promotion;

      // Reload promotions for the seller
      const promotionsResponse = await axios.get(
        `${API_BASE_URL}/promotions/seller/${sellerId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return {
        promotionRecord,
        promotions: promotionsResponse.data.promotions || [],
        updatedProducts: selectedProducts.map((product) => ({
          ...product,
          discountPercentage: discountType === 'percentage' ? Number(discount) || 0 : null,
        })),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to apply promotion');
    }
  }
);

export const deletePromotion = createAsyncThunk(
  'seller/deletePromotion',
  async ({ sellerId, promotionId }, { rejectWithValue }) => {
    try {
      if (!sellerId || !promotionId) {
        throw new Error('Missing promotion details');
      }

      await axios.delete(
        `${API_BASE_URL}/promotions/${promotionId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Reload promotions for the seller
      const response = await axios.get(
        `${API_BASE_URL}/promotions/seller/${sellerId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return {
        sellerId,
        promotionId, // Include promotionId so we can remove it from localStorage
        promotions: response.data.promotions || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to delete promotion');
    }
  }
);

export const updatePromotionStatus = createAsyncThunk(
  'seller/updatePromotionStatus',
  async ({ sellerId, promotionId, status }, { rejectWithValue }) => {
    try {
      if (!sellerId || !promotionId) {
        throw new Error('Missing promotion details');
      }

      await axios.put(
        `${API_BASE_URL}/promotions/${promotionId}/status`,
        { status },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Reload promotions for the seller
      const response = await axios.get(
        `${API_BASE_URL}/promotions/seller/${sellerId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return {
        sellerId,
        promotionId,
        status,
        promotions: response.data.promotions || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to update promotion status');
    }
  }
);

// Load cached orders immediately for instant chart rendering
const cachedOrders = loadCachedOrders();

const initialState = {
  // Reviews
  reviews: [],
  reviewsLoading: false,
  reviewsError: null,
  reviewsAnalytics: null,
  
  // Orders - initialize with cached data for instant chart loading
  orders: cachedOrders,
  ordersLoading: false,
  ordersError: null,
  pendingOrders: [],
  pendingOrdersLoading: false,
  pendingOrdersCount: 0,
  ordersToShip: [],
  
  // Products
  products: [],
  productsLoading: false,
  productsError: null,
  // Pagination for products
  productsCurrentPage: 0,
  productsTotalPages: 0,
  productsTotalElements: 0,
  productsPageSize: 20,
  pendingProducts: [],
  pendingProductsLoading: false,
  pendingProductsError: null,
  approvedProducts: [],
  approvedProductsLoading: false,
  approvedProductsError: null,
  rejectedProducts: [],
  rejectedProductsLoading: false,
  rejectedProductsError: null,
  createProductLoading: false,
  createProductError: null,
  updateProductLoading: false,
  updateProductError: null,
  deleteProductLoading: false,
  deleteProductError: null,
  duplicateProductLoading: false,
  duplicateProductError: null,
  
  // Stats
  stats: {
    totalProducts: { value: 0, trend: 0, period: '' },
    totalOrders: { value: 0, trend: 0, period: '' },
    monthlyRevenue: { value: 0, trend: 0, period: '' },
    averageRating: { value: 0, trend: 0, period: '' },
    pendingOrders: { value: 0 },
    pendingProducts: { value: 0 }
  },
  statsLoading: false,
  statsError: null,
  // Promotions
  promotions: [],
  promotionsLoading: false,
  promotionsError: null
};

const sellerSlice = createSlice({
  name: 'seller',
  initialState,
  reducers: {
    clearSellerData: (state) => {
      return initialState;
    },
    updatePendingCounts: (state, action) => {
      const { pendingOrders, pendingProducts } = action.payload;
      state.stats.pendingOrders.value = pendingOrders;
      state.stats.pendingProducts.value = pendingProducts;
    },
    setProductsCurrentPage: (state, action) => {
      state.productsCurrentPage = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch Seller Reviews
    builder
      .addCase(fetchSellerReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchSellerReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviews = action.payload.reviews || [];
        state.reviewsAnalytics = action.payload.analytics;
      })
      .addCase(fetchSellerReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      })
      // Merge the updated review returned by the reply/report APIs
      .addCase(addSellerResponse.fulfilled, (state, action) => {
        mergeUpdatedReview(state, action.payload);
      })
      .addCase(flagReview.fulfilled, (state, action) => {
        mergeUpdatedReview(state, action.payload);
      })
      
      // Fetch Seller Orders
      .addCase(fetchSellerOrders.pending, (state) => {
        state.ordersLoading = true;
        state.ordersError = null;
        // Keep cached orders visible while loading new data
        // Don't clear orders here - they will be updated when fetch completes
      })
      .addCase(fetchSellerOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        const freshOrders = action.payload.orders || [];
        state.orders = freshOrders;
        // Cache the fresh orders for instant loading on next visit
        saveCachedOrders(freshOrders);
      })
      .addCase(fetchSellerOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.ordersError = action.payload;
        // Keep cached orders visible even if fetch fails
      })
      
      // Fetch Seller Pending Orders (separate loading state to avoid blocking charts)
      // Use pendingOrdersLoading instead of ordersLoading to prevent blocking chart rendering
      .addCase(fetchSellerPendingOrders.pending, (state) => {
        state.pendingOrdersLoading = true;
      })
      .addCase(fetchSellerPendingOrders.fulfilled, (state, action) => {
        state.pendingOrdersLoading = false;
        state.pendingOrders = action.payload.orders || [];
        state.pendingOrdersCount = action.payload.count || 0;
        state.ordersToShip = action.payload.ordersToShip || [];
        // Update stats with pending orders count (non-blocking update)
        state.stats.pendingOrders = { value: action.payload.count || 0 };
      })
      .addCase(fetchSellerPendingOrders.rejected, (state, action) => {
        state.pendingOrdersLoading = false;
        state.ordersError = action.payload;
      })
      
      // Fetch Seller Products
      .addCase(fetchSellerProducts.pending, (state) => {
        state.productsLoading = true;
        state.productsError = null;
      })
      .addCase(fetchSellerProducts.fulfilled, (state, action) => {
        state.productsLoading = false;
        state.products = action.payload.products || action.payload.content || [];
        // Store pagination information - handle both Spring Page format and custom format
        state.productsCurrentPage = action.payload.currentPage !== undefined ? action.payload.currentPage : (action.payload.number || 0);
        state.productsTotalPages = action.payload.totalPages || 1;
        state.productsTotalElements = action.payload.totalElements || state.products.length;
        state.productsPageSize = action.payload.pageSize !== undefined ? action.payload.pageSize : (action.payload.size || 20);
      })
      .addCase(fetchSellerProducts.rejected, (state, action) => {
        state.productsLoading = false;
        state.productsError = action.payload;
      })
      
      // Fetch Seller Pending Products
      .addCase(fetchSellerPendingProducts.pending, (state) => {
        state.pendingProductsLoading = true;
        state.pendingProductsError = null;
      })
      .addCase(fetchSellerPendingProducts.fulfilled, (state, action) => {
        state.pendingProductsLoading = false;
        // Handle multiple response formats: products array, content array (Spring Page), or direct array
        const products = action.payload?.products || action.payload?.content || (Array.isArray(action.payload) ? action.payload : []);
        // Always set to array (even if empty) to mark as fetched
        state.pendingProducts = Array.isArray(products) ? products : [];
        state.stats.pendingProducts.value = state.pendingProducts.length;
      })
      .addCase(fetchSellerPendingProducts.rejected, (state, action) => {
        state.pendingProductsLoading = false;
        state.pendingProductsError = action.payload;
      })
      
      // Fetch Seller Approved Products
      .addCase(fetchSellerApprovedProducts.pending, (state) => {
        state.approvedProductsLoading = true;
        state.approvedProductsError = null;
      })
      .addCase(fetchSellerApprovedProducts.fulfilled, (state, action) => {
        state.approvedProductsLoading = false;
        // Handle multiple response formats: products array, content array (Spring Page), or direct array
        const products = action.payload?.products || action.payload?.content || (Array.isArray(action.payload) ? action.payload : []);
        // Always set to array (even if empty) to mark as fetched
        state.approvedProducts = Array.isArray(products) ? products : [];
      })
      .addCase(fetchSellerApprovedProducts.rejected, (state, action) => {
        state.approvedProductsLoading = false;
        state.approvedProductsError = action.payload;
      })
      // Rejected Products
      .addCase(fetchSellerRejectedProducts.pending, (state) => {
        state.rejectedProductsLoading = true;
        state.rejectedProductsError = null;
      })
      .addCase(fetchSellerRejectedProducts.fulfilled, (state, action) => {
        state.rejectedProductsLoading = false;
        // Handle multiple response formats: products array, content array (Spring Page), or direct array
        const products = action.payload?.products || action.payload?.content || (Array.isArray(action.payload) ? action.payload : []);
        // Always set to array (even if empty) to mark as fetched
        state.rejectedProducts = Array.isArray(products) ? products : [];
      })
      .addCase(fetchSellerRejectedProducts.rejected, (state, action) => {
        state.rejectedProductsLoading = false;
        state.rejectedProductsError = action.payload;
      })
      .addCase(loadSellerPromotions.pending, (state) => {
        state.promotionsLoading = true;
        state.promotionsError = null;
      })
      .addCase(loadSellerPromotions.fulfilled, (state, action) => {
        state.promotionsLoading = false;
        const promotions = action.payload || [];
        state.promotions = promotions;
        // Sync promotions to localStorage for cart page access
        const existingPromotions = loadStoredPromotions();
        const promotionMap = new Map();
        // Keep all existing promotions
        existingPromotions.forEach(p => {
          const pId = String(p.id || p._id || p.promotionId || '');
          promotionMap.set(pId, p);
        });
        // Add/update promotions from this seller (will overwrite if same ID)
        promotions.forEach(p => {
          const pId = String(p.id || p._id || p.promotionId || '');
          promotionMap.set(pId, p);
        });
        saveStoredPromotions(Array.from(promotionMap.values()));
        
        // Dispatch custom event to notify other tabs/pages about promotion update
        setTimeout(() => {
          window.dispatchEvent(new Event('promotionsUpdated'));
        }, 100);
      })
      .addCase(loadSellerPromotions.rejected, (state, action) => {
        state.promotionsLoading = false;
        state.promotionsError = action.payload || 'Failed to load promotions';
      })
      
      // Create Seller Product
      .addCase(createSellerProduct.pending, (state) => {
        state.createProductLoading = true;
        state.createProductError = null;
      })
      .addCase(createSellerProduct.fulfilled, (state, action) => {
        state.createProductLoading = false;
        const created = action.payload?.product || action.payload;
        if (created) {
          state.products = [created, ...state.products];
        }
      })
      .addCase(createSellerProduct.rejected, (state, action) => {
        state.createProductLoading = false;
        state.createProductError = action.payload || 'Failed to create product';
      })
      
      // Create Seller Product with Images
      .addCase(createSellerProductWithImages.pending, (state) => {
        state.createProductLoading = true;
        state.createProductError = null;
      })
      .addCase(createSellerProductWithImages.fulfilled, (state, action) => {
        state.createProductLoading = false;
        const created = action.payload?.product || action.payload;
        if (created) {
          state.products = [created, ...state.products];
        }
      })
      .addCase(createSellerProductWithImages.rejected, (state, action) => {
        state.createProductLoading = false;
        state.createProductError = action.payload || 'Failed to create product';
      })
      
      // Update Seller Product
      .addCase(updateSellerProduct.pending, (state) => {
        state.updateProductLoading = true;
        state.updateProductError = null;
      })
      .addCase(updateSellerProduct.fulfilled, (state, action) => {
        state.updateProductLoading = false;
        const updated = action.payload?.product || action.payload;
        if (updated) {
          // Update the product in both products and pendingProducts arrays
          const updateInArray = (array) => {
            const index = array.findIndex(p => p.id === updated.id);
            if (index !== -1) {
              array[index] = updated;
            }
          };
          updateInArray(state.products);
          updateInArray(state.pendingProducts);
        }
      })
      .addCase(updateSellerProduct.rejected, (state, action) => {
        state.updateProductLoading = false;
        state.updateProductError = action.payload || 'Failed to update product';
      })
      
      // Fetch Seller Stats
      .addCase(fetchSellerStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchSellerStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        // Only update stats with reviews analytics - pendingOrders and products are fetched separately
        // This allows dashboard to load faster without waiting for slow pendingOrders call
        if (action.payload.reviews && action.payload.reviews.analytics) {
          state.stats = {
            ...state.stats,
            averageRating: {
              ...state.stats.averageRating,
              value: action.payload.reviews.analytics.averageRating || state.stats.averageRating.value
            }
            // Keep existing other stats (pendingOrders, pendingProducts will be updated by their respective thunks)
          };
        }
      })
      .addCase(fetchSellerStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })
      // Apply Promotion to products
      .addCase(applyPromotion.pending, (state) => {
        state.promotionsLoading = true;
        state.promotionsError = null;
      })
      .addCase(applyPromotion.fulfilled, (state, action) => {
        state.promotionsLoading = false;
        const payload = action.payload || {};
        const { updatedProducts, promotions } = payload;

        if (Array.isArray(updatedProducts) && updatedProducts.length) {
          const map = new Map(
            state.products.map((p) => [String(p.id ?? p._id), p])
          );
          updatedProducts.forEach((product) => {
            const key = String(product.id ?? product._id);
            if (map.has(key)) {
              map.set(key, { ...map.get(key), ...product });
            } else {
              map.set(key, product);
            }
          });
          state.products = Array.from(map.values());
        }

        if (Array.isArray(promotions)) {
          state.promotions = promotions;
          // Sync promotions to localStorage for cart page access
          const existingPromotions = loadStoredPromotions();
          const promotionMap = new Map();
          // Keep all existing promotions
          existingPromotions.forEach(p => {
            const pId = String(p.id || p._id || p.promotionId || '');
            promotionMap.set(pId, p);
          });
          // Add/update promotions (will overwrite if same ID)
          promotions.forEach(p => {
            const pId = String(p.id || p._id || p.promotionId || '');
            promotionMap.set(pId, p);
          });
          saveStoredPromotions(Array.from(promotionMap.values()));
          
          // Dispatch custom event to notify other tabs/pages about promotion update
          setTimeout(() => {
            window.dispatchEvent(new Event('promotionsUpdated'));
          }, 100);
        }
      })
      .addCase(applyPromotion.rejected, (state, action) => {
        state.promotionsLoading = false;
        state.promotionsError = action.payload || 'Failed to apply promotion';
      })
      .addCase(deletePromotion.pending, (state) => {
        state.promotionsLoading = true;
        state.promotionsError = null;
      })
      .addCase(deletePromotion.fulfilled, (state, action) => {
        state.promotionsLoading = false;
        const { promotions, promotionId, sellerId } = action.payload || {};
        if (Array.isArray(promotions)) {
          state.promotions = promotions;
          // Sync promotions to localStorage for cart page access
          // Remove deleted promotion and sync remaining promotions
          const existingPromotions = loadStoredPromotions();
          const promotionMap = new Map();
          
          // Create a set of promotion IDs from backend response for quick lookup
          const backendPromotionIds = new Set(
            promotions.map(p => String(p.id || p._id || p.promotionId || ''))
          );
          
          const deletedId = String(promotionId || '');
          const currentSellerId = String(sellerId || '');
          
          // Remove deleted promotion and any promotions not in backend response
          // This ensures deleted promotions are completely removed
          existingPromotions.forEach(p => {
            const pId = String(p.id || p._id || p.promotionId || '');
            
            // Skip the deleted promotion explicitly
            if (pId === deletedId) {
              return;
            }
            
            // Keep promotions from other sellers (check by sellerId)
            const pSellerId = String(p.sellerId || '');
            
            // If promotion is from a different seller, keep it
            if (pSellerId && pSellerId !== currentSellerId) {
              promotionMap.set(pId, p);
              return;
            }
            
            // For promotions from this seller, only keep if they're in backend response
            if (backendPromotionIds.has(pId)) {
              promotionMap.set(pId, p);
            }
          });
          
          // Add/update promotions from this seller (from backend response)
          promotions.forEach(p => {
            const pId = String(p.id || p._id || p.promotionId || '');
            promotionMap.set(pId, p);
          });
          
          saveStoredPromotions(Array.from(promotionMap.values()));
          
          // Dispatch custom event to notify other tabs/pages about promotion deletion
          // Use a small delay to ensure localStorage is updated first
          setTimeout(() => {
            window.dispatchEvent(new Event('promotionsUpdated'));
          }, 100);
        }
      })
      .addCase(deletePromotion.rejected, (state, action) => {
        state.promotionsLoading = false;
        state.promotionsError = action.payload || 'Failed to delete promotion';
      })
      .addCase(updatePromotionStatus.pending, (state) => {
        state.promotionsLoading = true;
        state.promotionsError = null;
      })
      .addCase(updatePromotionStatus.fulfilled, (state, action) => {
        state.promotionsLoading = false;
        const { promotions } = action.payload || {};
        if (Array.isArray(promotions)) {
          state.promotions = promotions;
          // Sync promotions to localStorage for cart page access
          const existingPromotions = loadStoredPromotions();
          const promotionMap = new Map();
          // Keep all existing promotions
          existingPromotions.forEach(p => {
            const pId = String(p.id || p._id || p.promotionId || '');
            promotionMap.set(pId, p);
          });
          // Add/update promotions (will overwrite if same ID)
          promotions.forEach(p => {
            const pId = String(p.id || p._id || p.promotionId || '');
            promotionMap.set(pId, p);
          });
          saveStoredPromotions(Array.from(promotionMap.values()));
          
          // Dispatch custom event to notify other tabs/pages about promotion update
          setTimeout(() => {
            window.dispatchEvent(new Event('promotionsUpdated'));
          }, 100);
        }
      })
      .addCase(updatePromotionStatus.rejected, (state, action) => {
        state.promotionsLoading = false;
        state.promotionsError = action.payload || 'Failed to update promotion status';
      })
      
      // Delete Seller Product
      .addCase(deleteSellerProduct.pending, (state) => {
        state.deleteProductLoading = true;
        state.deleteProductError = null;
      })
      .addCase(deleteSellerProduct.fulfilled, (state, action) => {
        state.deleteProductLoading = false;
        const deletedProductId = action.payload.productId;
        // Remove the product from all arrays
        state.products = state.products.filter(p => p.id !== deletedProductId);
        state.pendingProducts = state.pendingProducts.filter(p => p.id !== deletedProductId);
        state.approvedProducts = state.approvedProducts.filter(p => p.id !== deletedProductId);
      })
      .addCase(deleteSellerProduct.rejected, (state, action) => {
        state.deleteProductLoading = false;
        state.deleteProductError = action.payload || 'Failed to delete product';
      })
      
      // Duplicate Seller Product
      .addCase(duplicateSellerProduct.pending, (state) => {
        state.duplicateProductLoading = true;
        state.duplicateProductError = null;
      })
      .addCase(duplicateSellerProduct.fulfilled, (state, action) => {
        state.duplicateProductLoading = false;
        const duplicated = action.payload?.product || action.payload;
        if (duplicated) {
          // Add the duplicated product to the products array
          state.products = [duplicated, ...state.products];
          // If it's pending, also add to pendingProducts
          if (duplicated.approvalStatus === 'PENDING') {
            state.pendingProducts = [duplicated, ...state.pendingProducts];
          }
        }
      })
      .addCase(duplicateSellerProduct.rejected, (state, action) => {
        state.duplicateProductLoading = false;
        state.duplicateProductError = action.payload || 'Failed to duplicate product';
      });
  }
});

export const { clearSellerData, updatePendingCounts, setProductsCurrentPage } = sellerSlice.actions;
export default sellerSlice.reducer;